/**
 * Turning a `category` + `output` pair into a sentence a person can read.
 *
 * Harder than it looks, because the two lists in `functions/src/prompts.ts` are
 * not uniform. `OUTPUT_TYPES` mixes things you name ("Restaurant Name"), things
 * you write ("Dating Profile Bio", "Protest Sign") and things you invent
 * ("Fake Medical Condition"), and `CATEGORY_WORDS` mixes singular, plural and
 * mass nouns. Any wording has to survive all of them.
 *
 * "Name a ..." was wrong twice over: it does not apply to half the outputs (you
 * do not name a Bad Yelp Review) and it stutters on the fifteen that already
 * end in Name or Title. "Create a ..." works everywhere.
 *
 * Everything the UI says about the prompt comes from here, so changing the
 * wording is one edit rather than a hunt through components.
 */

/**
 * Words that begin with a vowel letter but a consonant sound, so they take "a".
 * "Universe" is the live case; the rest are here because the lists get curated
 * and this is the trap nobody remembers.
 */
const CONSONANT_SOUNDING_VOWELS = /^(uni|use|user|uti|utensil|euro|eu|one|once)/i;

/** The mirror case: a consonant letter with a vowel sound, so it takes "an". */
const VOWEL_SOUNDING_CONSONANTS = /^(hour|honest|honou?r|heir)/i;

/** `a` or `an`, decided by sound rather than by spelling. */
export function indefiniteArticle(phrase: string): 'a' | 'an' {
  const word = phrase.trim();
  if (!word) return 'a';

  if (VOWEL_SOUNDING_CONSONANTS.test(word)) return 'an';
  if (CONSONANT_SOUNDING_VOWELS.test(word)) return 'a';

  return /^[aeiou]/i.test(word) ? 'an' : 'a';
}

export interface PromptPhrasing {
  /** "Create a" or "Create an". */
  lead: string;
  /** The thing being made, shown as its own tile. */
  output: string;
  /** "with a" or "with an". */
  join: string;
  /** The theme, shown as its own tile. */
  category: string;
  /** "twist." */
  tail: string;
}

/**
 * The prompt as five parts, so the two variable halves can be rendered as
 * tiles while the connecting words stay in body type.
 */
export function phrasePrompt(category: string, output: string): PromptPhrasing {
  return {
    lead: `Create ${indefiniteArticle(output)}`,
    output,
    join: `with ${indefiniteArticle(category)}`,
    category,
    tail: 'twist.',
  };
}

/** The same sentence as one string, for page titles and notification text. */
export function promptSentence(category: string, output: string): string {
  const { lead, join, tail } = phrasePrompt(category, output);
  return `${lead} ${output} ${join} ${category} ${tail}`;
}
